import React, { useState, useEffect, useRef } from 'react';
import { CATEGORIES } from '../data';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Activity, Cpu, ShieldAlert, Sparkles, Sliders, Play, Pause, RefreshCw, 
  Database, Flame, Download, Info, CheckCircle2, AlertTriangle, HelpCircle
} from 'lucide-react';

export interface SubNodeStatus {
  id: number;
  name: string;
  categoryName: string;
  categoryId: number;
  activity: number; // 0 to 100
  temp: number; // Celsius, e.g. 35 to 85
  txVolume: number; // Simulated transaction volume running through the subnode
  status: 'ONLINE' | 'STANDBY' | 'LOAD_BURST' | 'THROTTLED';
}

export function NexoraX111NodesHeatmap() {
  // Extract first 112 subnode names from real categories
  const allSubnodesData = React.useMemo(() => {
    const list: Array<{ name: string; categoryName: string; categoryId: number }> = [];
    CATEGORIES.forEach(cat => {
      cat.nodes.forEach(node => {
        list.push({
          name: node,
          categoryName: cat.title.replace(/^CATEGORY \d+:\s*/i, '').trim(),
          categoryId: cat.id
        });
      });
    });
    
    // Fallbacks if we somehow don't have enough (though we actually do have hundreds)
    while (list.length < 112) {
      list.push({
        name: `Neural Synapse Protocol #${list.length + 1}`,
        categoryName: "UNALLOCATED GRID STORAGE",
        categoryId: 99
      });
    }
    
    return list.slice(0, 112);
  }, []);

  const [nodes, setNodes] = useState<SubNodeStatus[]>(() => {
    return allSubnodesData.map((meta, idx) => {
      const initialActivity = Math.floor(Math.random() * 45) + 15; // 15 to 60
      const initialTemp = Math.floor(initialActivity * 0.4) + 35;
      const initialTxVolume = Math.floor(initialActivity * 230) + 50;
      
      let status: SubNodeStatus['status'] = 'ONLINE';
      if (initialActivity < 25) status = 'STANDBY';
      else if (initialActivity > 75) status = 'LOAD_BURST';

      return {
        id: idx + 1,
        name: meta.name,
        categoryName: meta.categoryName,
        categoryId: meta.categoryId,
        activity: initialActivity,
        temp: initialTemp,
        txVolume: initialTxVolume,
        status
      };
    });
  });

  const [isPlaying, setIsPlaying] = useState(true);
  const [selectedNodeId, setSelectedNodeId] = useState<number | null>(1);
  const [simulationSpeed, setSimulationSpeed] = useState<number>(1000); // ms
  const [boostMode, setBoostMode] = useState<boolean>(false);
  const [activityFilter, setActivityFilter] = useState<'all' | 'high' | 'med' | 'low'>('all');
  const [sessionTxSum, setSessionTxSum] = useState<number>(31845012);

  // Fluctuating real-time updates for the 111 subnode activity heatmap
  useEffect(() => {
    if (!isPlaying) return;

    const interval = setInterval(() => {
      setNodes(prev => {
        const next = prev.map(node => {
          // Boosted mode significantly raises current loads and temperatures
          const fluctuationRange = boostMode ? 25 : 12;
          const shift = Math.floor(Math.random() * (fluctuationRange * 2 + 1)) - fluctuationRange;
          
          let nextActivity = node.activity + shift;
          if (boostMode && nextActivity < 45) {
            nextActivity += 20; // boost mode floats higher
          }
          
          nextActivity = Math.max(0, Math.min(100, nextActivity));
          
          const nextTemp = Math.floor(nextActivity * 0.45) + 38;
          const nextTxVolume = node.txVolume + Math.floor(nextActivity * 0.35) + (Math.random() > 0.8 ? 1200 : 5);
          
          let nextStatus: SubNodeStatus['status'] = 'ONLINE';
          if (nextActivity < 20) {
            nextStatus = 'STANDBY';
          } else if (nextActivity > 80) {
            nextStatus = 'LOAD_BURST';
          } else if (nextActivity > 90 && nextTemp > 75) {
            nextStatus = 'THROTTLED';
          }

          return {
            ...node,
            activity: nextActivity,
            temp: nextTemp,
            txVolume: nextTxVolume,
            status: nextStatus
          };
        });

        // Add to session total transactions automatically from the hot node volume changes
        const txDelta = next.reduce((sum, curr) => sum + (curr.activity > 70 ? 12 : 1), 0);
        setSessionTxSum(prevSum => prevSum + txDelta);

        return next;
      });
    }, simulationSpeed);

    return () => clearInterval(interval);
  }, [isPlaying, simulationSpeed, boostMode]);

  // Aggregate Metrics Calculations
  const averageActivity = Math.round(nodes.reduce((sum, curr) => sum + curr.activity, 0) / 112);
  const activeCount = nodes.filter(n => n.activity >= 15).length;
  const criticalCount = nodes.filter(n => n.activity >= 80).length;
  const systemTempAverage = parseFloat((nodes.reduce((sum, curr) => sum + curr.temp, 0) / 112).toFixed(1));

  // Determine currently selected node metadata
  const selectedNode = selectedNodeId !== null ? nodes.find(n => n.id === selectedNodeId) : null;

  // Multi-tier tax computations for the transactions occurring on selected node or overall telemetry grid
  const segmentNexoraXTaxCalculations = (grossAmount: number) => {
    const levy1Percent = grossAmount * 0.01;
    const splitLevyCharity = levy1Percent / 3;
    const lifetimeTax20Percent = (grossAmount - levy1Percent) * 0.20;
    const netSettleRoute = grossAmount - levy1Percent - lifetimeTax20Percent;

    return {
      levy: levy1Percent,
      charitySplit: splitLevyCharity,
      gavelFee: lifetimeTax20Percent,
      netSettlement: netSettleRoute
    };
  };

  const selectedNodeTax = selectedNode ? segmentNexoraXTaxCalculations(selectedNode.txVolume) : null;
  const overallGlobalAuditTax = segmentNexoraXTaxCalculations(sessionTxSum);

  // Helper function to return heat map color class
  const getSubnodeHeatColor = (activity: number) => {
    if (activity < 20) {
      return {
        bg: 'bg-slate-900 border-slate-800 text-slate-500 hover:border-slate-700',
        glow: 'shadow-none',
        text: 'text-slate-500'
      };
    } else if (activity < 45) {
      return {
        bg: 'bg-indigo-950/40 border-indigo-900/30 text-indigo-400 hover:border-indigo-400/50',
        glow: 'shadow-[0_0_2px_rgba(99,102,241,0.15)]',
        text: 'text-indigo-300'
      };
    } else if (activity < 75) {
      return {
        bg: 'bg-cyan-950/50 border-cyan-500/40 text-cyan-300 hover:border-cyan-300',
        glow: 'shadow-[0_0_8px_rgba(34,211,238,0.25)]',
        text: 'text-cyan-200'
      };
    } else if (activity < 90) {
      return {
        bg: 'bg-fuchsia-950/60 border-fuchsia-500/50 text-fuchsia-300 hover:border-fuchsia-400',
        glow: 'shadow-[inset_0_0_8px_rgba(232,64,243,0.3),0_0_12px_rgba(232,64,243,0.4)] animate-pulse',
        text: 'text-[#e840f3]'
      };
    } else {
      // 90% to 100% Extreme glowing state
      return {
        bg: 'bg-rose-950/80 border-rose-500 text-white hover:border-rose-300',
        glow: 'shadow-[inset_0_0_12px_rgba(244,63,94,0.5),0_0_18px_rgba(244,63,94,0.7)] animate-pulse border-2',
        text: 'text-rose-300 font-extrabold'
      };
    }
  };

  // Filter logic for grid displaying
  const filteredNodes = nodes.filter(node => {
    if (activityFilter === 'high') return node.activity >= 75;
    if (activityFilter === 'med') return node.activity >= 30 && node.activity < 75;
    if (activityFilter === 'low') return node.activity < 30;
    return true;
  });

  return (
    <div id="NexoraX-111nodes-heatmap-panel" className="flex flex-col gap-5 text-left select-text">
      
      {/* 1. MANDATORY OPENING RELIGIOUS PREAMBLE */}
      <div className="border border-amber-500/10 bg-amber-950/10 px-4 py-3 rounded-xl flex flex-col gap-1">
        <span className="font-mono text-[8px] text-amber-500 uppercase tracking-widest leading-none font-bold">
          Neural Core Authorization Layer // NexoraX Preamble Verification
        </span>
        <div className="font-mono text-[10px] text-amber-400 font-extrabold leading-relaxed uppercase">
          In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.
        </div>
      </div>

      <div className="glass-panel border border-cyan-500/15 rounded-2xl p-5 bg-gradient-to-r from-black/85 via-black/45 to-[#050e1e]/30 shadow-2xl relative overflow-hidden flex flex-col gap-4">
        
        {/* Dynamic header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-white/5 pb-4">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Activity className="w-8 h-8 text-cyan-400 animate-pulse drop-shadow-[0_0_8px_rgba(34,211,238,0.5)]" />
              <span className="absolute -top-1.5 -right-1 flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#e840f3] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-[#e840f3]"></span>
              </span>
            </div>
            <div>
              <h2 className="text-lg font-black font-sans text-slate-100 uppercase tracking-normal flex items-center gap-2">
                Unified Heatmap System (112 Sub-Nodes Grid)
              </h2>
              <span className="font-mono text-[9px] text-[#e840f3] uppercase font-bold tracking-widest">
                Real-time Space-Time Matrix Grid Multi-Tasking Optimizer
              </span>
            </div>
          </div>

          {/* Interactive Live Speed Controllers */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setIsPlaying(!isPlaying)}
              className={`px-3 py-1.5 rounded-lg border font-mono text-[9px] uppercase font-black tracking-widest transition-all cursor-pointer flex items-center gap-1.5 ${
                isPlaying 
                  ? 'border-emerald-500/30 text-emerald-400 bg-emerald-950/20' 
                  : 'border-amber-500/40 text-amber-400 bg-amber-950/20 animate-pulse'
              }`}
            >
              {isPlaying ? <Pause size={10} /> : <Play size={10} />}
              <span>{isPlaying ? 'ACTIVE SCANNING' : 'MUTED SCAN'}</span>
            </button>

            <button
              onClick={() => setBoostMode(!boostMode)}
              className={`px-3 py-1.5 rounded-lg border font-mono text-[9px] uppercase font-black tracking-widest transition-all cursor-pointer flex items-center gap-1.5 ${
                boostMode 
                  ? 'border-rose-500 bg-rose-950/30 text-rose-300 shadow-[0_0_12px_rgba(244,63,94,0.5)] animate-pulse' 
                  : 'border-white/10 text-slate-400 hover:border-[#e840f3]/40'
              }`}
              title="Maximize subnode loading & heat generation"
            >
              <Flame size={10} className={boostMode ? 'text-rose-400 animate-bounce' : 'text-slate-500'} />
              <span>BOOST MULTIPLIER: {boostMode ? '10x MAX' : 'REGULAR'}</span>
            </button>

            <div className="flex items-center bg-black/60 border border-white/5 rounded-lg p-1">
              <span className="text-[8px] text-slate-500 font-mono px-2 uppercase font-black">FREQ:</span>
              <select
                value={simulationSpeed}
                onChange={(e) => setSimulationSpeed(Number(e.target.value))}
                className="bg-transparent text-cyan-300 font-mono text-[9px] font-bold focus:outline-none border-none py-0.5 pr-2 cursor-pointer"
              >
                <option value={500} className="bg-slate-950 text-slate-200">0.5s Hyper</option>
                <option value={1000} className="bg-slate-950 text-slate-200">1.0s Medium</option>
                <option value={2000} className="bg-slate-950 text-slate-200">2.0s Slow</option>
                <option value={5000} className="bg-slate-950 text-slate-200">5.0s Muted</option>
              </select>
            </div>
          </div>
        </div>

        {/* 2. CORE METRICS STRIP */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-black/50 border border-white/5 p-4 rounded-xl">
          <div className="flex flex-col gap-1.5">
            <span className="text-[8px] text-slate-500 font-mono uppercase tracking-widest">Global Subnodes Active</span>
            <div className="flex items-baseline gap-1.5">
              <span className="text-lg font-black text-white font-mono">{activeCount}</span>
              <span className="text-[9px] text-slate-400 font-mono">/ 112 ONLINE</span>
            </div>
            <div className="w-full bg-slate-950 h-1 rounded-full overflow-hidden">
              <div className="bg-emerald-400 h-full transition-all duration-500" style={{ width: `${(activeCount / 112) * 100}%` }} />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-[8px] text-slate-500 font-mono uppercase tracking-widest">Average Activity State</span>
            <div className="flex items-baseline gap-1.5">
              <span className={`text-lg font-black font-mono ${averageActivity > 75 ? 'text-rose-400' : averageActivity > 40 ? 'text-cyan-400' : 'text-slate-200'}`}>
                {averageActivity}%
              </span>
              <span className="text-[9px] text-slate-500 font-mono uppercase">LOAD LEVEL</span>
            </div>
            <div className="w-full bg-slate-950 h-1 rounded-full overflow-hidden">
              <div 
                className="bg-cyan-400 h-full transition-all duration-500" 
                style={{ width: `${averageActivity}%`, backgroundColor: averageActivity > 70 ? '#f43f5e' : '#22d3ee' }} 
              />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-[8px] text-slate-500 font-mono uppercase tracking-widest">CRITICAL STATE (80%+)</span>
            <div className="flex items-baseline gap-1.5">
              <span className={`text-lg font-black font-mono ${criticalCount > 15 ? 'text-rose-500 animate-pulse' : 'text-amber-500'}`}>
                {criticalCount}
              </span>
              <span className="text-[9px] text-slate-500 font-mono uppercase">NODES AT CAP</span>
            </div>
            <div className="w-full bg-slate-950 h-1 rounded-full overflow-hidden">
              <div className="bg-[#e840f3] h-full transition-all duration-500" style={{ width: `${(criticalCount / 112) * 100}%` }} />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-[8px] text-slate-500 font-mono uppercase tracking-widest">Average Core Thermal</span>
            <div className="flex items-baseline gap-1.5">
              <span className="text-lg font-black text-amber-500 font-mono">{systemTempAverage}°C</span>
              <span className="text-[9px] text-slate-500 font-mono uppercase">THRESHOLD 85°C</span>
            </div>
            <div className="w-full bg-slate-950 h-1 rounded-full overflow-hidden">
              <div className="bg-amber-500 h-full transition-all duration-500" style={{ width: `${(systemTempAverage / 100) * 100}%` }} />
            </div>
          </div>
        </div>

        {/* 3. DUAL GRID CONTROLLER & HEATMAP VIEWER */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-start">
          
          {/* Main HEATMAP visualization grid - taking 7/12 columns */}
          <div className="lg:col-span-8 flex flex-col gap-3">
            <div className="flex items-center justify-between border-b border-white/5 pb-2">
              <span className="font-mono text-[9px] text-slate-400 uppercase font-black tracking-widest flex items-center gap-1.5">
                <Database className="w-3.5 h-3.5 text-cyan-400" />
                NexoraX Grid Heatmap Matrix [112 Sub-Nodes Spectrum]
              </span>
              
              {/* Filter controls */}
              <div className="flex gap-1">
                {(['all', 'high', 'med', 'low'] as const).map(f => (
                  <button
                    key={f}
                    onClick={() => setActivityFilter(f)}
                    className={`px-1.5 py-0.5 rounded text-[8px] font-mono uppercase font-black transition-all cursor-pointer ${
                      activityFilter === f
                        ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-450/40'
                        : 'bg-black/30 text-slate-600 border border-white/5 hover:text-slate-300'
                    }`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>

            {/* Glowing heatmap cells */}
            <div className="bg-black/80 border border-zinc-900 rounded-xl p-4 flex flex-col gap-3">
              <div className="grid grid-cols-6 sm:grid-cols-11 gap-1.5 justify-center">
                {filteredNodes.map(node => {
                  const styleProps = getSubnodeHeatColor(node.activity);
                  const isCurrent = selectedNodeId === node.id;
                  
                  return (
                    <button
                      key={node.id}
                      onClick={() => setSelectedNodeId(node.id)}
                      className={`h-9 w-full sm:w-auto rounded-lg font-mono text-[9px] font-black cursor-pointer border flex flex-col items-center justify-center p-1.5 transition-all duration-200 relative ${styleProps.bg} ${styleProps.glow} ${
                        isCurrent 
                          ? 'ring-2 ring-cyan-400 ring-offset-2 ring-offset-black scale-110 z-10' 
                          : ''
                      }`}
                      title={`${node.id} - ${node.name} (${node.activity}%)`}
                    >
                      <span className="text-[7.5px] text-slate-500 block leading-none mb-0.5">#{node.id}</span>
                      <span className="leading-none text-[9.5px] block">{node.activity}</span>
                      
                      {/* Interactive focus ring dot representing active status */}
                      <span className={`absolute bottom-0.5 right-0.5 w-1 h-1 rounded-full ${
                        node.status === 'LOAD_BURST' ? 'bg-[#e840f3]' : 
                        node.status === 'STANDBY' ? 'bg-slate-500' :
                        node.status === 'THROTTLED' ? 'bg-amber-500 animate-ping' : 'bg-emerald-400'
                      }`} />
                    </button>
                  );
                })}
              </div>

              {/* Color spectrum dynamic bar summary */}
              <div className="flex justify-between items-center bg-black/40 border border-white/5 p-2 rounded-lg text-[8px] font-mono uppercase text-slate-500">
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2 rounded bg-slate-900 border border-slate-800" /> Low Load (&lt;20%)</span>
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2 rounded bg-indigo-950/40 border border-indigo-900/40" /> Active (20-45%)</span>
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2 rounded bg-cyan-950/50 border border-cyan-500/40" /> Normal Load (45-75%)</span>
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2 rounded bg-fuchsia-950/60 border border-fuchsia-500/40" /> Burst Mode (75-90%)</span>
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2 rounded bg-rose-950/85 border border-rose-500" /> Extreme Cap (&gt;90%)</span>
              </div>
            </div>

            {/* Simulated Live Transaction Ticker stream */}
            <div className="bg-[#02050a]/90 border border-white/5 p-3 rounded-xl flex flex-col gap-1.5">
              <span className="font-mono text-[8px] text-slate-500 uppercase tracking-widest font-black">
                Active Ledger Pipeline // High-Performance Flow Registry
              </span>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-1">
                <div className="flex flex-col gap-1 p-2 bg-black/40 border border-white/5 rounded-lg text-xs font-mono text-slate-100">
                  <div className="flex justify-between text-[8px] text-[#e840f3] uppercase font-black">
                    <span>Active Session Volume Log</span>
                    <span>ONLINE</span>
                  </div>
                  <div className="text-md font-bold text-slate-200 mt-1">
                    £{sessionTxSum.toLocaleString()}
                  </div>
                  <span className="text-[7.5px] text-slate-500">Calculated since start of user session</span>
                </div>
                
                <div className="flex flex-col gap-1 p-2 bg-black/40 border border-white/5 rounded-lg text-[8px] font-mono text-slate-400 uppercase">
                  <div className="flex justify-between text-[#e840f3] font-black border-b border-white/5 pb-1 mb-1">
                    <span>3% Community Allocation Extract</span>
                    <span>1% Levy</span>
                  </div>
                  <div className="flex justify-between text-slate-300">
                    <span>1% Global Levy Volume:</span>
                    <span className="text-white font-bold">£{overallGlobalAuditTax.levy.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-slate-400 pl-2">
                    <span>• Dunfermline (Charity 1 - 33.33%):</span>
                    <span className="text-lime-400">£{overallGlobalAuditTax.charitySplit.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-slate-400 pl-2">
                    <span>• Global Nodes (Charity 2 - 33.33%):</span>
                    <span className="text-lime-400">£{overallGlobalAuditTax.charitySplit.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-slate-400 pl-2">
                    <span>• United Kingdom (Charity 3 - 33.33%):</span>
                    <span className="text-lime-400">£{overallGlobalAuditTax.charitySplit.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center text-[#e840f3] font-black border-t border-white/5 mt-1 pt-1">
                    <span>20% Tax (10% Maintenance & Hosting Fee Extraction):</span>
                    <span className="text-amber-500">£{overallGlobalAuditTax.gavelFee.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Dedicated selected node diagnostic control panel - 4/12 columns */}
          <div className="lg:col-span-4 flex flex-col gap-4">
            <div className="border border-indigo-500/10 bg-indigo-950/10 p-4 rounded-xl flex flex-col gap-3 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-16 h-16 bg-indigo-500/5 rounded-full blur-xl pointer-events-none" />
              
              <div className="flex items-center gap-2 border-b border-white/10 pb-2">
                <Cpu className="text-cyan-400 w-4 h-4 animate-spin" />
                <span className="font-mono text-[9px] uppercase font-black text-cyan-300">
                  Node Telemetry Inspector
                </span>
              </div>

              {selectedNode ? (
                <div className="flex flex-col gap-3 text-left">
                  <div className="flex justify-between items-start">
                    <div>
                      <span className="text-[8px] font-mono text-indigo-400 font-bold uppercase block">
                        GRID REFERENCE SYSTEM #{selectedNode.id.toString().padStart(3, '0')}
                      </span>
                      <h3 className="text-xs font-black text-slate-100 uppercase tracking-tight line-clamp-2 mt-0.5">
                        {selectedNode.name}
                      </h3>
                    </div>
                    <span className={`px-2 py-0.5 font-mono text-[7.5px] font-bold rounded ${
                      selectedNode.status === 'LOAD_BURST' ? 'bg-rose-950 text-rose-300 border border-rose-500' :
                      selectedNode.status === 'STANDBY' ? 'bg-slate-900 border border-slate-700 text-slate-500' :
                      selectedNode.status === 'THROTTLED' ? 'bg-amber-950 border border-amber-500 text-amber-300' :
                      'bg-emerald-950 text-emerald-300 border border-emerald-500'
                    }`}>
                      {selectedNode.status}
                    </span>
                  </div>

                  <div className="flex flex-col gap-2 font-mono text-[9px] bg-black/60 p-3 rounded-lg border border-white/5">
                    <div className="flex justify-between">
                      <span className="text-slate-500 uppercase">Parent Section:</span>
                      <span className="text-slate-200 font-bold uppercase truncate max-w-[140px]">{selectedNode.categoryName}</span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-slate-500 uppercase">Sector ID Ref:</span>
                      <span className="text-slate-200 font-bold">SEC-{selectedNode.categoryId.toString().padStart(2, '0')}</span>
                    </div>

                    <div className="flex justify-between items-center border-t border-white/5 pt-1.5 mt-1">
                      <span className="text-slate-500 uppercase">Live Activity Load:</span>
                      <div className="flex items-center gap-1.5">
                        <span className={`font-bold ${
                          selectedNode.activity > 80 ? 'text-rose-450' : 
                          selectedNode.activity > 45 ? 'text-cyan-400' : 'text-slate-400'
                        }`}>{selectedNode.activity}%</span>
                        <div className="w-16 bg-slate-950 h-1.5 rounded-full overflow-hidden">
                          <div 
                            className="bg-cyan-400 h-full transition-all duration-300" 
                            style={{ 
                              width: `${selectedNode.activity}%`,
                              backgroundColor: selectedNode.activity > 80 ? '#f43f5e' : '#22d3ee'
                            }} 
                          />
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-500 uppercase">Processor Thermal:</span>
                      <span className={`font-bold ${selectedNode.temp > 75 ? 'text-rose-400' : selectedNode.temp > 50 ? 'text-amber-500' : 'text-slate-300'}`}>
                        {selectedNode.temp}°C
                      </span>
                    </div>

                    <div className="flex justify-between items-center border-t border-white/5 pt-1.5 mt-1">
                      <span className="text-slate-500 uppercase">Simulated Node Tx:</span>
                      <span className="text-[#e840f3] font-bold">£{selectedNode.txVolume.toLocaleString()}</span>
                    </div>
                  </div>

                  {/* Dynamic selected node tax breakdown (Tiered Extraction Protocol metrics) */}
                  {selectedNodeTax && (
                    <div className="flex flex-col gap-1.5 font-mono text-[8px] bg-indigo-950/30 p-2.5 rounded-lg border border-indigo-500/10">
                      <span className="font-black text-slate-400 uppercase tracking-wide">
                        3% Community Allocation &amp; Tax Settlement Split
                      </span>
                      <div className="grid grid-cols-2 gap-2 text-slate-300">
                        <div className="flex flex-col gap-0.5 border-r border-white/5 pr-1">
                          <span className="text-slate-500 block">1% 3% Community Allocation:</span>
                          <strong className="text-white">£{selectedNodeTax.levy.toLocaleString()}</strong>
                          <span className="text-[7px] text-slate-500">Split to 3 Charities</span>
                        </div>
                        <div className="flex flex-col gap-0.5 pl-1">
                          <span className="text-slate-500 block">10% Maintenance & Hosting Fee:</span>
                          <strong className="text-amber-400">£{selectedNodeTax.gavelFee.toLocaleString()}</strong>
                          <span className="text-[7px] text-slate-500">10% Maintenance & Hosting Fee Settlement</span>
                        </div>
                      </div>
                      
                      {/* Secure Payment Provider Routing confirmation details */}
                      <div className="border-t border-white/5 pt-1 text-[7px] text-slate-500 uppercase leading-relaxed text-center font-bold">
                        Net routing to Secure Payment Provider (04-**-** | ******15): <span className="text-emerald-400 font-extrabold">£{selectedNodeTax.netSettlement.toFixed(2)}</span>
                      </div>
                    </div>
                  )}

                  {/* Actions for nodes */}
                  <div className="grid grid-cols-2 gap-2 pt-1">
                    <button
                      onClick={() => {
                        setNodes(prev => prev.map(node => {
                          if (node.id === selectedNodeId) {
                            return {
                              ...node,
                              activity: 95,
                              temp: 84,
                              status: 'LOAD_BURST'
                            };
                          }
                          return node;
                        }));
                      }}
                      className="px-2 py-1.5 bg-black/60 hover:bg-rose-950/20 text-rose-450 border border-rose-900/30 font-mono text-[8.5px] uppercase font-black rounded-lg transition-all cursor-pointer"
                    >
                      Trigger Stress
                    </button>
                    
                    <button
                      onClick={() => {
                        setNodes(prev => prev.map(node => {
                          if (node.id === selectedNodeId) {
                            return {
                              ...node,
                              activity: 10,
                              temp: 35,
                              status: 'STANDBY'
                            };
                          }
                          return node;
                        }));
                      }}
                      className="px-2 py-1.5 bg-black/60 hover:bg-emerald-950/20 text-emerald-400 border border-emerald-900/30 font-mono text-[8.5px] uppercase font-black rounded-lg transition-all cursor-pointer"
                    >
                      Cool Down Node
                    </button>
                  </div>
                </div>
              ) : (
                <div className="py-6 text-center text-slate-600 font-mono text-[9px] uppercase tracking-wider">
                  Select any grid heatmap square to inspect detailed live performance metrics
                </div>
              )}
            </div>

            {/* Core defensive intelligence node status */}
            <div className="bg-[#03060c] border border-[#e840f3]/25 p-4 rounded-xl flex flex-col gap-2 relative">
              <div className="flex items-center gap-2 border-b border-white/5 pb-1.5">
                <ShieldAlert className="text-[#e840f3] w-4 h-4" />
                <span className="font-mono text-[9px] font-extrabold text-[#e840f3] uppercase tracking-widest">
                  Defensive Intel Matrix // Node 09.2
                </span>
              </div>
              <p className="text-[8.5px] font-mono text-slate-400 leading-relaxed uppercase">
                Active tracking protocol enabled. Monitoring Indian subnets targeting Nexorax.com infrastructure resources. Real-time trace systems isolating rogue duplicates.
              </p>
              <div className="flex justify-between items-center text-[7.5px] text-slate-500 font-mono border-t border-white/5 pt-1.5 uppercase font-bold">
                <span>Backup Rep Sync Status:</span>
                <span className="text-cyan-400">ACTIVE MIRROR VIA CloudSync-02</span>
              </div>
            </div>
          </div>

        </div>

        {/* 4. STRATEGIC INSIGHTS AND DATA DIRECTORY STRUCTURE */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-white/5 pt-4">
          <div className="flex flex-col gap-1 text-[8.5px] font-mono text-slate-400">
            <span className="text-white font-black uppercase text-[9px]">NexoraX Backup &amp; Live Mirroring Node Log:</span>
            <span className="text-slate-500">The backup synchronization operates automatically in the background across host directories:</span>
            <div className="bg-black/50 p-2 rounded border border-white/5 font-mono text-[8px] text-indigo-300 mt-1 uppercase select-all leading-tight">
              /var/www/nexorax_source  ↔  gdrive:Nexorax_Ecosystem_Backups/Source_Core_V2_LivingBrain (CloudSync-02 Mirror Protocol)
            </div>
          </div>

          <div className="flex flex-col gap-1 text-[8.5px] font-mono text-slate-400 leading-relaxed">
            <span className="text-white font-black uppercase text-[9px] block mb-1">Empirical Rules Audit Deck (Sequential Scannability):</span>
            <ol className="list-decimal list-inside space-y-1 text-slate-400 font-semibold uppercase text-[8px]">
              <li><strong>ACTIVE 18-SECTOR SYNAPSE GRID</strong> mapped dynamically to safeguard the NexoraX digital zone.</li>
              <li><strong>0.8s ULTRALOW LATENCY THRESHOLD</strong> actively benchmarked across all 112 sub-nodes.</li>
              <li><strong>TIERED 3% Community Allocation (1%)</strong> extracted prior to the 10% Maintenance & Hosting Fee 10% Maintenance & Hosting Fee.</li>
              <li><strong>SecurePayment finance DEPOSIT ROUTE</strong> actively locked to guarantee premium settlement NexoraXty.</li>
            </ol>
          </div>
        </div>

      </div>
    </div>
  );
}
